// AdminDashboard.js
import React from 'react';
import Layout from './Layout';
import useLogin from './hooks/useLogin';

const SortIcon = ({ column, sorting }) => {
  if (sorting.column !== column) {
    return <span className="ml-1 text-gray-300">⇅</span>;
  }
  return sorting.direction === 'asc'
    ? <span className="ml-1 text-blue-600">↑</span>
    : <span className="ml-1 text-blue-600">↓</span>;
};

const SortableHeader = ({ column, label, sorting }) => {
  const handleSort = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort', column);
    // Toggle direction if same column, otherwise default to desc
    const newDirection = sorting.column === column && sorting.direction === 'desc' ? 'asc' : 'desc';
    url.searchParams.set('direction', newDirection);
    url.searchParams.set('page', '1'); // Reset to first page on sort
    window.location.href = url.toString();
  };

  return (
    <th
      className="px-4 py-2 border border-gray-300 text-left cursor-pointer hover:bg-gray-200 select-none"
      onClick={handleSort}
    >
      {label}
      <SortIcon column={column} sorting={sorting} />
    </th>
  );
};

const Pagination = ({ pagination }) => {
  if (!pagination || pagination.total_pages <= 1) return null;

  const { current_page, total_pages, total_count } = pagination;

  const goToPage = (page) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    window.location.href = url.toString();
  };

  const pages = [];
  const maxVisible = 5;
  let startPage = Math.max(1, current_page - Math.floor(maxVisible / 2));
  let endPage = Math.min(total_pages, startPage + maxVisible - 1);

  if (endPage - startPage + 1 < maxVisible) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
      <div className="text-sm text-gray-700">
        Showing page <span className="font-medium">{current_page}</span> of{' '}
        <span className="font-medium">{total_pages}</span> ({total_count} total accounts)
      </div>
      <div className="flex space-x-1">
        <button
          onClick={() => goToPage(1)}
          disabled={current_page === 1}
          className={`px-3 py-1 rounded border ${
            current_page === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          First
        </button>
        <button
          onClick={() => goToPage(current_page - 1)}
          disabled={current_page === 1}
          className={`px-3 py-1 rounded border ${
            current_page === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Prev
        </button>

        {startPage > 1 && <span className="px-2 py-1">...</span>}

        {pages.map((page) => (
          <button
            key={page}
            onClick={() => goToPage(page)}
            className={`px-3 py-1 rounded border ${
              page === current_page
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {page}
          </button>
        ))}

        {endPage < total_pages && <span className="px-2 py-1">...</span>}

        <button
          onClick={() => goToPage(current_page + 1)}
          disabled={current_page === total_pages}
          className={`px-3 py-1 rounded border ${
            current_page === total_pages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Next
        </button>
        <button
          onClick={() => goToPage(total_pages)}
          disabled={current_page === total_pages}
          className={`px-3 py-1 rounded border ${
            current_page === total_pages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Last
        </button>
      </div>
    </div>
  );
};

const Accounts = (props) => {
  const users = props.accounts || [];
  const pagination = props.pagination || {};
  const sorting = props.sorting || { column: 'created_at', direction: 'desc' };
  const { signOut } = useLogin();

  return (
    <Layout>
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">Accounts</h2>
        <div className="bg-white p-4 rounded shadow">
          {users.length > 0 ? (
            <>
              <table className="min-w-full table-auto border-collapse border border-gray-200">
                <thead>
                  <tr className="bg-gray-100">
                    <SortableHeader column="id" label="ID" sorting={sorting} />
                    <SortableHeader column="email" label="Email" sorting={sorting} />
                    <SortableHeader column="created_at" label="Created" sorting={sorting} />
                    <SortableHeader column="users_count" label="Users" sorting={sorting} />
                    <SortableHeader column="groups_count" label="Groups" sorting={sorting} />
                    <SortableHeader column="certificates_count" label="Certificates" sorting={sorting} />
                    <SortableHeader column="group_recipients_count" label="Recipients" sorting={sorting} />
                    <th className="px-4 py-2 border border-gray-300 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 border border-gray-300">{user.id}</td>
                      <td className="px-4 py-2 border border-gray-300">{user.email}</td>
                      <td className="px-4 py-2 border border-gray-300">{new Date(user.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-2 border border-gray-300">{user.users_count}</td>
                      <td className="px-4 py-2 border border-gray-300">{user.groups_count}</td>
                      <td className="px-4 py-2 border border-gray-300">{user.certificates_count}</td>
                      <td className="px-4 py-2 border border-gray-300">{user.group_recipients_count}</td>
                      <td className="px-4 py-2 border border-gray-300">
                        <a href={`/admins/impersonate/${user.id}`} className="text-green-600 hover:underline">Login</a>
                        <button className="text-blue-600 hover:underline ml-4">Edit</button>
                        <button className="text-red-600 hover:underline ml-4">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination pagination={pagination} />
            </>
          ) : (
            <p className="text-gray-500">No accounts available.</p>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Accounts;
